import React, { useState } from 'react';
import { IonIcon, IonText } from '@ionic/react';
import { chevronDownOutline, chevronUpOutline, documentTextOutline, informationCircleOutline } from 'ionicons/icons';
import { Attachment, LineItemDetail, PlanBackground } from '@/types/model/inspection';
import { apiService } from '@/services/ApiService';

interface InspectionDetailsDrawerProps {
  lineItemDetail: LineItemDetail;
  inspectionPlanBackground: PlanBackground;
}

interface DetailRowProps {
  label: string;
  value: string | number | null | undefined;
}

const sectionStyle: React.CSSProperties = {
  background: 'white',
  border: '1px solid var(--ion-color-light-shade)',
  borderRadius: '12px',
  padding: '1rem',
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.75rem',
  cursor: 'pointer',
};

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.75rem', alignItems: 'start' }}>
    <IonText color="medium">
      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{label}</span>
    </IonText>
    <span style={{ fontSize: '0.95rem', lineHeight: 1.5 }}>{formatValue(value)}</span>
  </div>
);

const formatValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  if (typeof value === 'string' && value.trim() === '') {
    return 'N/A';
  }

  return String(value);
};

const getAttachmentHref = (attachment: Attachment): string | null => {
  if (attachment.Link) {
    return attachment.Link;
  }

  if (attachment.FileId) {
    return apiService.generateAttachmentDownloadUrl(attachment.FileId);
  }

  return null;
};

const renderAttachments = (attachments: Attachment[] | null | undefined): React.ReactNode => {
  if (!attachments || attachments.length === 0) {
    return <span style={{ fontSize: '0.95rem' }}>N/A</span>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {attachments.map(attachment => {
        const href = getAttachmentHref(attachment);
        const label = attachment.Filename || attachment.Description || `File ${attachment.FileId}`;

        if (!href) {
          return (
            <span key={attachment.Id} style={{ fontSize: '0.95rem' }}>
              {label}
            </span>
          );
        }

        return (
          <a
            key={attachment.Id}
            href={href}
            target="_blank"
            rel="noreferrer"
            style={{
              color: 'var(--ion-color-primary)',
              textDecoration: 'none',
              fontSize: '0.95rem',
              wordBreak: 'break-word',
            }}
          >
            {label}
          </a>
        );
      })}
    </div>
  );
};

const InspectionDetailsDrawer: React.FC<InspectionDetailsDrawerProps> = ({
  lineItemDetail,
  inspectionPlanBackground,
}) => {
  const [isPlanBackgroundExpanded, setIsPlanBackgroundExpanded] = useState(true);
  const [isLineItemDetailExpanded, setIsLineItemDetailExpanded] = useState(true);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      
      {/* Inspection Plan Background Section */}
      <div style={sectionStyle}>
        <div
          style={{ ...sectionHeaderStyle, marginBottom: isPlanBackgroundExpanded ? '1rem' : 0 }}
          onClick={() => setIsPlanBackgroundExpanded(prev => !prev)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IonIcon icon={informationCircleOutline} style={{ color: 'var(--ion-color-primary)', fontSize: '1.1rem' }} />
            <IonText>
              <span style={{ fontWeight: 700 }}>Inspection Plan Background</span>
            </IonText>
          </div>
          <IonIcon
            icon={isPlanBackgroundExpanded ? chevronUpOutline : chevronDownOutline}
            style={{ color: 'var(--ion-color-medium)', fontSize: '1.1rem', flexShrink: 0 }}
          />
        </div>

        {isPlanBackgroundExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            <DetailRow label="Primary Wt." value={inspectionPlanBackground.LineItemPrimaryWeight} />
            <DetailRow label="Alternate Wt." value={inspectionPlanBackground.LineItemAlternateWeight} />
            <DetailRow label="Time to Inspect" value={inspectionPlanBackground.TimeToInspect} />
            <DetailRow label="Method" value={inspectionPlanBackground.MethodOfInspection} />
            <DetailRow label="Sample Size" value={inspectionPlanBackground.SampleSize} />
            <DetailRow label="Sample Sel." value={inspectionPlanBackground.SampleSelection} />
            <DetailRow label="Instructions" value={inspectionPlanBackground.AdditionalInstructions} />
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.75rem', alignItems: 'start' }}>
              <IonText color="medium">
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Attachments</span>
              </IonText>
              {renderAttachments(inspectionPlanBackground.InspectionPlanAttachments)}
            </div>
          </div>
        )}
      </div>

      {/* Line Item Detail Section */}
      <div style={sectionStyle}>
        <div
          style={{ ...sectionHeaderStyle, marginBottom: isLineItemDetailExpanded ? '1rem' : 0 }}
          onClick={() => setIsLineItemDetailExpanded(prev => !prev)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IonIcon icon={documentTextOutline} style={{ color: 'var(--ion-color-primary)', fontSize: '1.1rem' }} />
            <IonText>
              <span style={{ fontWeight: 700 }}>Line Item Detail</span>
            </IonText>
          </div>
          <IonIcon
            icon={isLineItemDetailExpanded ? chevronUpOutline : chevronDownOutline}
            style={{ color: 'var(--ion-color-medium)', fontSize: '1.1rem', flexShrink: 0 }}
          />
        </div>

        {isLineItemDetailExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            <DetailRow label="Major Req." value={lineItemDetail.MajorRequirement} />
            <DetailRow label="Related Info" value={lineItemDetail.RelatedInfo} />
            <DetailRow label="Performance" value={lineItemDetail.PerformanceStandard} />
            <DetailRow label="Est. Quantity" value={lineItemDetail.EstimatedQuantity} />
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.75rem', alignItems: 'start' }}>
              <IonText color="medium">
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Attachments</span>
              </IonText>
              {renderAttachments(lineItemDetail.LineItemAttachments)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionDetailsDrawer;